import { useCallback, useEffect, useRef, useState } from "react";
import {
  useShellRequest,
  isHttpUrl,
  PAGE_TIMEOUT_MS,
  type ActivePage,
  type ShellHeader,
  type ShellMethod,
  type ShellPageErrorKind,
  type ShellRequestMode,
  type ShellRequest,
  type LastRequest,
  type OpenTab,
  type ShellCaptureBinding,
} from "../hooks/useShellRequest";

interface ShellBrowserPanelProps {
  sessionId: string;
  sessionToken: string;
  onClose: () => void;
}

const METHODS: readonly ShellMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;

/** Body characters shown before the "Voir tout" toggle. Display only — the
 *  hook always keeps the full body. */
export const BODY_PREVIEW_CHARS = 2000;

/** Panel height, aligned on the AudioDebugPanel (170) / LogsPanel (200) row. */
const PANEL_HEIGHT = 220;

const PAGE_ERROR_LABELS: Record<ShellPageErrorKind, string> = {
  invalid_url: "URL refusée",
  opaque: "Réponse opaque (cross-origin)",
  timeout: `Timeout page (${PAGE_TIMEOUT_MS / 1000} s)`,
  cors_or_network: "CORS ou erreur réseau — refus du navigateur",
  page: "Erreur dans la page",
};

/**
 * Header names the Fetch specification forbids a page from setting. Chrome
 * decides alone: such a row is never a guarantee, so the UI says so instead of
 * pretending the value was sent.
 */
const BROWSER_CONTROLLED_HEADERS: readonly string[] = [
  "accept-charset",
  "accept-encoding",
  "access-control-request-headers",
  "access-control-request-method",
  "connection",
  "content-length",
  "cookie",
  "cookie2",
  "date",
  "dnt",
  "expect",
  "host",
  "keep-alive",
  "origin",
  "referer",
  "set-cookie",
  "te",
  "trailer",
  "transfer-encoding",
  "upgrade",
  "user-agent",
  "via",
] as const;

/** True when Chrome, not the page, owns this header. */
export function isBrowserControlledHeader(name: string): boolean {
  const key = name.trim().toLowerCase();
  if (!key) return false;
  if (key.startsWith("proxy-") || key.startsWith("sec-")) return true;
  return BROWSER_CONTROLLED_HEADERS.includes(key);
}

/**
 * Headers the backend relay refuses outright in guest mode: framing and
 * hop-by-hop ones, plus `Cookie` — the backend is its sole owner there, and it
 * rejects a user value rather than merging into it.
 */
const RELAY_REJECTED_HEADERS: readonly string[] = [
  "connection",
  "content-length",
  "cookie",
  "expect",
  "host",
  "keep-alive",
  "proxy-authenticate",
  "proxy-authorization",
  "te",
  "trailer",
  "transfer-encoding",
  "upgrade",
] as const;

/** True when the backend relay rejects this header instead of forwarding it. */
export function isRelayRejectedHeader(name: string): boolean {
  const key = name.trim().toLowerCase();
  if (!key) return false;
  if (key.startsWith("proxy-")) return true;
  return RELAY_REJECTED_HEADERS.includes(key);
}

const MODE_SUBTITLES: Record<ShellRequestMode, string> = {
  in_page:
    "Exécuté dans la page ; CORS et les politiques de cookies du navigateur s'appliquent.",
  guest_proxy:
    "Exécuté dans la VM : IP de sortie et cookies de la session rejoués, sans parité TLS/HTTP avec Chrome.",
};

/**
 * Rebuild the original bytes of a captured body (one code unit per byte).
 * Returns null when a code unit escapes the byte range — the caller must not
 * pretend it holds bytes it does not.
 */
export function capturedBodyBytes(raw: string): Uint8Array | null {
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    const code = raw.charCodeAt(i);
    if (code > 255) return null;
    bytes[i] = code;
  }
  return bytes;
}

/**
 * Task 213 — relative age of a capture, computed at render.
 *
 * No timer: a panel that re-renders every second to keep "il y a 12 s" honest
 * would repaint the whole form for a cosmetic label. The value refreshes on the
 * next render, which is enough for a list the user reads then acts on.
 *
 * `timestamp` is epoch SECONDS — both bridges carry CDP `wallTime` unchanged.
 */
export function relativeTime(timestamp: number | null, nowMs: number): string {
  if (timestamp === null || !Number.isFinite(timestamp)) return "date inconnue";
  const seconds = Math.round(nowMs / 1000 - timestamp);
  // A clock skew between guest and host must not print "il y a -4 s".
  if (seconds < 1) return "\u00e0 l\u2019instant";
  if (seconds < 60) return "il y a " + seconds + " s";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return "il y a " + minutes + " min";
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return "il y a " + hours + " h";
  return "il y a " + Math.floor(hours / 24) + " j";
}

/**
 * Decide whether a captured body can be shown as text.
 *
 * Valid UTF-8 is not enough: C0/C1 control characters would garble the panel
 * and hide what the body really is. Tab, LF and CR are the exceptions every
 * text body legitimately contains.
 */
export function decodeCapturedBody(raw: string): { text: string } | { bytes: number } {
  const bytes = capturedBodyBytes(raw);
  if (!bytes) return { bytes: raw.length };
  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return { bytes: bytes.length };
  }
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    const printable =
      char === "\t" || char === "\n" || char === "\r"
        ? true
        : !(code < 0x20 || (code >= 0x7f && code <= 0x9f));
    if (!printable) return { bytes: bytes.length };
  }
  return { text };
}

/**
 * Task 219 — which open tab is the one the browser shows, without lying.
 *
 * Prefer the actual evaluated CDP target, even if the tab list is stale.
 * With an older bridge, identity is resolved by matching (`url` + `title`)
 * against the list. A unique URL+title match wins; failing that, a unique URL
 * match (the list's title may lag behind `document.title`). Several candidates
 * left is an honest "unknown": the first one is never picked, no marker is ever
 * written into the page to tell them apart, and the caller skips the capture
 * path rather than proposing a neighbour's capture.
 */
export function resolveActiveTabId(page: ActivePage, tabs: readonly OpenTab[]): string | null {
  if (typeof page.tabId === "string" && page.tabId !== "") return page.tabId;
  const byUrlAndTitle = tabs.filter((tab) => tab.url === page.url && tab.title === page.title);
  if (byUrlAndTitle.length === 1) return (byUrlAndTitle[0] as OpenTab).tabId;
  if (byUrlAndTitle.length > 1) return null;
  const byUrl = tabs.filter((tab) => tab.url === page.url);
  return byUrl.length === 1 ? (byUrl[0] as OpenTab).tabId : null;
}

/** Task 219 — lifecycle of one « Sync B→SB », rendered next to the button. */
type SyncState = "idle" | "loading" | "page_unavailable" | "cookies_unavailable";

const inputStyle: React.CSSProperties = {
  background: "#111827",
  color: "#e5e7eb",
  border: "1px solid #374151",
  borderRadius: 3,
  fontSize: 11,
  padding: "2px 6px",
};

export default function ShellBrowserPanel({
  sessionId,
  sessionToken,
  onClose,
}: ShellBrowserPanelProps) {
  const {
    loading,
    response,
    pageError,
    transportError,
    send,
    lastRequest,
    lastRequestStatus,
    getLastRequest,
    requestHistory,
    requestHistoryStatus,
    getRequestHistory,
    getRequestHistoryEntry,
    openTabs,
    openTabsStatus,
    getOpenTabs,
    getActivePage,
    getCookieHeader,
    snapshot,
    getSnapshot,
  } = useShellRequest(sessionId, sessionToken);

  // A captured method may fall outside the five static options.
  const [method, setMethod] = useState<string>("GET");
  const [url, setUrl] = useState("");
  const [headers, setHeaders] = useState<ShellHeader[]>([{ key: "", value: "" }]);
  const [body, setBody] = useState("");
  const [bodyPresent, setBodyPresent] = useState(false);
  // Task 212 — the bytes exactly as captured, kept beside the visible text.
  // The textarea may show a decoded or masked version; this is what is sent.
  const [capturedBodyRaw, setCapturedBodyRaw] = useState<string | null>(null);
  const [captureBodyIssue, setCaptureBodyIssue] = useState<"unavailable" | "truncated" | null>(null);
  // Task 215 — renamed from `expanded`: this one is the response body's
  // "Voir tout", and must never be confused with the panel disclosure below.
  const [responseBodyExpanded, setResponseBodyExpanded] = useState(false);
  // In-page stays the default: it is the mode that reproduces the browser.
  const [mode, setMode] = useState<ShellRequestMode>("in_page");
  // Task 219 — the panel opens expanded, directly usable. 215 opened it
  // collapsed on purpose; the user found the banner too easy to miss, and the
  // decision was reversed. The disclosure still collapses it at will. Nothing
  // is persisted: `SessionView` unmounts the panel when it closes.
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);
  const [selectedCapture, setSelectedCapture] = useState<LastRequest | null>(null);
  const [dirty, setDirty] = useState(false);
  const [followNotice, setFollowNotice] = useState("");
  const [followMode, setFollowMode] = useState<"browser" | "tab" | "history">("browser");
  const lastActiveRef = useRef<string | null>(null);
  const draftsRef = useRef(new Map<string, {method:string;url:string;headers:ShellHeader[];body:string;
    raw:string|null;capture:LastRequest|null;issue:"unavailable"|"truncated"|null;bodyPresent:boolean}>());
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [historyEntryState, setHistoryEntryState] = useState<"idle" | "loading" | "missing">(
    "idle",
  );
  // Task 219 — « Sync B→SB »: disabled while reading, then re-armed even on
  // failure. A failure is said next to the button; it never touches the form.
  const [syncState, setSyncState] = useState<SyncState>("idle");
  const mountedRef = useRef(true);
  // Armed by the first load, whatever triggered it. Collapsing and expanding
  // again must not re-read: the data stays visible in the collapsed banner.
  const sourcesLoadedRef = useRef(false);
  // Task 215 — incremente a CHAQUE selection, onglet comme capture. Une lecture
  // de detail encore en vol quand l'utilisateur choisit autre chose ne doit ni
  // remplir le formulaire qu'il vient de reconstruire, ni annoncer « entree
  // expiree » a propos d'une selection qu'il a abandonnee.
  const selectionRef = useRef(0);
  // Task 219 — le jeton du DERNIER Sync. Distinct de `selectionRef` : un Sync
  // supplante par un clic d'onglet doit quand meme rearmer son bouton, alors
  // qu'un Sync supplante par un Sync plus recent ne doit pas toucher l'etat
  // que ce dernier possede desormais.
  const syncTokenRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    selectionRef.current += 1;
    draftsRef.current.clear();
    setSelectedTabId(null); setSelectedCapture(null); setDirty(false);
    setUrl(""); setBody(""); setBodyPresent(false); setCapturedBodyRaw(null); setHeaders([{key:"",value:""}]);
    setFollowMode("browser"); lastActiveRef.current = null;
    return () => {mountedRef.current = false; selectionRef.current += 1;};
  }, [sessionId, sessionToken]);

  const markEdited = () => {
    selectionRef.current += 1;
    setDirty(true);
    setFollowNotice("Brouillon conservé ; les nouvelles captures ne l'écrasent pas.");
  };
  const stashDraft = () => {
    if (dirty && selectedTabId) {
      if (draftsRef.current.size >= 32 && !draftsRef.current.has(selectedTabId)) {
        setFollowNotice("Limite de 32 brouillons atteinte : sélection conservée pour ne pas perdre votre édition.");
        return false;
      }
      draftsRef.current.set(selectedTabId,
        {method,url,headers,body,raw:capturedBodyRaw,capture:selectedCapture,issue:captureBodyIssue,bodyPresent});
    }
    return true;
  };

  const updateHeader = (index: number, patch: Partial<ShellHeader>) => {
    if (JSON.stringify(headers.map((row,i)=>i === index ? {...row,...patch} : row)).length > 65536) {
      setFollowNotice("En-têtes limités à 64 KiB."); return;
    }
    markEdited();
    setHeaders((rows) =>
      rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };
  const addHeader = () => {markEdited(); setHeaders((rows) => rows.length < 256 ? [...rows, { key: "", value: "" }] : rows);};
  const removeHeader = (index: number) => {
    markEdited(); setHeaders((rows) => rows.filter((_, i) => i !== index));
  };

  /**
   * Task 215/219 — the three reads, together, through one door.
   *
   * The mount (219), the first expansion after a manual collapse and the
   * explicit refresh all come through here, and all arm the same ref, so a
   * mount followed by a collapse and an expansion reads once, not twice.
   */
  const loadSources = useCallback(() => {
    sourcesLoadedRef.current = true;
    if (getSnapshot) {void getSnapshot(); return;}
    void getOpenTabs();
    void getLastRequest();
    void getRequestHistory();
  }, [getOpenTabs, getLastRequest, getRequestHistory, getSnapshot]);

  /**
   * Task 219 — the panel opens expanded, so the sources are read at mount: the
   * same `loadSources()`, the same guard, no second code path.
   *
   * The cleanup releases the guard on purpose. In development React StrictMode
   * replays mount → cleanup → mount; the hook's own cleanup has by then closed
   * the sockets of the first read without settling any state, so a guard left
   * armed would freeze the three sources on « lecture… » until a manual
   * refresh. Releasing it makes the replayed mount read again — one live read,
   * never two. In production and in tests the effect runs once. The same
   * release lets a session token that arrives after the mount (page reload)
   * trigger the read the empty token could not.
   */
  useEffect(() => {
    if (!sourcesLoadedRef.current) loadSources();
    return () => {
      sourcesLoadedRef.current = false;
    };
  }, [loadSources]);

  const toggleDetails = () => {
    const next = !detailsExpanded;
    setDetailsExpanded(next);
    // Replier demonte l'omnibox : sans ceci, `suggestionsOpen` resterait vrai et
    // le depliage suivant rouvrirait la liste sans que personne ne l'ait
    // demandee, deja posee sur la colonne Requete.
    if (!next) setSuggestionsOpen(false);
    if (next && !sourcesLoadedRef.current) loadSources();
  };

  // A refresh that drops the selected tab clears the filter, nothing else: the
  // URL the user is editing belongs to them, not to the tab list.
  useEffect(() => {
    if (selectedTabId === null || openTabsStatus !== "available") return;
    if (!openTabs.some((tab) => tab.tabId === selectedTabId && !tab.closed))
      setFollowNotice("Onglet fermé ou indisponible : capture conservée, envoi interdit.");
  }, [openTabs, openTabsStatus, selectedTabId]);

  const submit = () => {
    setResponseBodyExpanded(false);
    setSuggestionsOpen(false);
    // An untouched capture is replayed byte for byte, whatever the panel shows.
    if (selectedTabId && !openTabs.some(tab=>tab.tabId === selectedTabId && !tab.closed)) {
      setFollowNotice("Onglet fermé : envoi interdit."); return;
    }
    const payload: ShellRequest =
      capturedBodyRaw === null
        ? { method, url, headers, body, bodyPresent }
        : { method, url, headers, body: capturedBodyRaw, bodyKind: "binary_string", bodyPresent:true };
    const binding: ShellCaptureBinding | undefined = selectedCapture?.capture
      ? {...selectedCapture.capture, edited:dirty} : undefined;
    void send(payload, mode, selectedTabId ?? undefined, binding);
  };

  const fillFromCapture = (captured: LastRequest) => {
    setBodyPresent(captured.bodyState === "complete" || (captured.bodyState === undefined && captured.body !== null && captured.body !== ""));
    setSelectedCapture(captured);
    setDirty(false);
    setFollowNotice("");
    setCaptureBodyIssue(captured.body === null ? "unavailable" : captured.bodyTruncated ? "truncated" : null);
    setMethod(captured.method);
    setUrl(captured.url);
    const rows = Object.entries(captured.headers).map(([key, value]) => ({ key, value }));
    setHeaders(rows.length ? rows : [{ key: "", value: "" }]);
    // A body Chrome could not provide must not become an empty one.
    const raw = captured.body;
    if (raw === null || captured.bodyState === "absent") {
      setCapturedBodyRaw(null);
      setBody("");
      return;
    }
    setCapturedBodyRaw(raw);
    const decoded = decodeCapturedBody(raw);
    // Binary stays unrendered: showing mojibake would misrepresent the bytes,
    // and the raw value is preserved for the send either way.
    setBody("text" in decoded ? decoded.text : "");
  };

  const useLastRequest = () => {
    if (!lastRequest) return;
    if (lastRequest.capture) {if (!stashDraft()) return; setSelectedTabId(lastRequest.capture.tabId);}
    else if (getSnapshot) {setFollowNotice("Cette lecture ancienne n'a pas d'identité de capture."); return;}
    fillFromCapture(lastRequest);
  };

  /**
   * Task 215 — one place where picking a tab happens, whether the click came
   * from the tab strip or from a suggestion.
   *
   * It sets the filter and prefills a fresh GET. It does NOT change the mode,
   * does not switch the real tab, and sends nothing.
   */
  const selectTab = (tab: OpenTab) => {
    if (!stashDraft()) return;
    selectionRef.current += 1;
    setFollowMode("tab");
    setSelectedTabId(tab.tabId);
    setSuggestionsOpen(false);
    const draft = draftsRef.current.get(tab.tabId);
    if (draft) {
      setMethod(draft.method); setUrl(draft.url); setHeaders(draft.headers); setBody(draft.body);
      setCapturedBodyRaw(draft.raw); setSelectedCapture(draft.capture); setCaptureBodyIssue(draft.issue);
      setBodyPresent(draft.bodyPresent);
      setDirty(true); setFollowNotice("Brouillon de cet onglet restauré."); return;
    }

    // Task 217 — la capture la plus recente DE CET ONGLET, s'il y en a une.
    // `requestHistory` est deja newest-first et rendu tel quel par le hook, donc
    // le premier element trouve est le plus recent. L'appariement se fait sur
    // `tabId` (identite 214), jamais sur l'URL : deux onglets peuvent partager
    // une URL, et proposer la capture du voisin serait pire que ne rien
    // proposer. Aucune lecture bridge supplementaire : l'historique est deja
    // charge par `loadSources()`.
    const captured = requestHistory.find((entry) => entry.tabId === tab.tabId);
    if (captured) {
      // Le meme chemin que la selection d'historique, appele tel quel : jeton
      // `selectionRef`, chargement du detail, remplissage par `fillFromCapture`.
      // Dupliquer cette logique ici la ferait deriver de son original.
      selectHistoryEntry(captured.id, true);
      return;
    }

    // Rien a charger pour cet onglet : le GET vierge d'origine, inchange.
    setCaptureBodyIssue(null);
    setSelectedCapture(null); setDirty(false);
    setFollowNotice(tab.closed ? "Onglet fermé : envoi interdit." : "Aucune requête encore capturée pour cet onglet.");
    setMethod("GET");
    setUrl(tab.url);
    setHeaders([{ key: "", value: "" }]);
    setBody("");
    setBodyPresent(false);
    setCapturedBodyRaw(null);
    setHistoryEntryState("idle");
  };

  /**
   * Task 213/215 — load one capture in full, then fill through the very same
   * function as "Utiliser". An expired id says so and leaves the form alone.
   * The filter and the mode are untouched.
   */
  const selectHistoryEntry = (id: number, automatic = false) => {
    if (!automatic) {if (!stashDraft()) return; setFollowMode("history");}
    const expectedTab = requestHistory.find(row=>row.id === id)?.tabId;
    if (expectedTab) setSelectedTabId(expectedTab);
    const token = ++selectionRef.current;
    setSuggestionsOpen(false);
    setHistoryEntryState("loading");
    void getRequestHistoryEntry(id).then((entry) => {
      if (!mountedRef.current || selectionRef.current !== token) return;
      if (!entry) {
        setHistoryEntryState("missing");
        return;
      }
      if (expectedTab && entry.tabId !== expectedTab) {setHistoryEntryState("missing"); return;}
      setSelectedTabId(entry.tabId);
      setHistoryEntryState("idle");
      fillFromCapture(entry);
    });
  };

  /**
   * Task 219 — « Sync B→SB »: fill the form from the tab Chrome really shows.
   *
   * 1. Read the active page (209's mechanism), the open tabs and the history —
   *    fresh, and from the returned arrays, never from the React state that
   *    will only update later.
   * 2. Resolve the tab identity without guessing (`resolveActiveTabId`). If it
   *    is known and a capture exists for that `tabId`, delegate to the very
   *    same path as a click on the entry: `selectHistoryEntry`. Full fidelity,
   *    no second copy of `fillFromCapture`.
   * 3. Otherwise fill honestly with what is really known: the URL, `GET`, and
   *    at most one `Cookie` line built from cookies actually read. No Referer,
   *    no Sec-Fetch-*, no User-Agent, nothing plausible.
   *
   * Every `await` is followed by the mounted + token check: a tab click, a
   * history entry or a newer Sync wins over an older answer. The button is
   * disabled while reading and re-armed even on failure, without touching the
   * form. The execution mode is never changed.
   */
  const syncFromBrowser = async () => {
    if (getSnapshot) {
      if (!stashDraft()) return;
      setDirty(false); setFollowMode("browser");
      setSelectedCapture(null); lastActiveRef.current = null;
      await getSnapshot(); return;
    }
    const token = ++selectionRef.current;
    syncTokenRef.current = token;
    setSyncState("loading");
    setSuggestionsOpen(false);
    try {
      const [page, tabs, history] = await Promise.all([
        getActivePage(),
        getOpenTabs(),
        getRequestHistory(),
      ]);
      if (!mountedRef.current || selectionRef.current !== token) return;
      if (!page) {
        setSyncState("page_unavailable");
        return;
      }

      const tabId = resolveActiveTabId(page, tabs);
      if (tabId !== null) {
        setSelectedTabId(tabId);
        // Newest-first, as the hook renders it: the first match is the latest.
        const captured = history.find((entry) => entry.tabId === tabId);
        if (captured) {
          // Release the button while this token still owns it:
          // `selectHistoryEntry` takes the selection token over.
          setSyncState("idle");
          selectHistoryEntry(captured.id);
          return;
        }
      }

      // No capture, or an identity too ambiguous to trust: only what is real.
      // A non-http(s) page (about:blank, chrome://…) has no cookies to read.
      const cookieHeader = isHttpUrl(page.url) ? await getCookieHeader(page.url) : "";
      if (!mountedRef.current || selectionRef.current !== token) return;
      setCaptureBodyIssue(null);
      setMethod("GET");
      setUrl(page.url);
      setHeaders(cookieHeader ? [{ key: "Cookie", value: cookieHeader }] : [{ key: "", value: "" }]);
      setBody("");
      setCapturedBodyRaw(null);
      setHistoryEntryState("idle");
      setSyncState(cookieHeader === null ? "cookies_unavailable" : "idle");
    } finally {
      // Superseded by a tab or an entry: the button is re-armed all the same.
      // Superseded by a newer Sync: that one owns the state now — hands off.
      if (mountedRef.current && syncTokenRef.current === token) {
        setSyncState((state) => (state === "loading" ? "idle" : state));
      }
    }
  };

  const capturedBytes = capturedBodyRaw === null ? null : capturedBodyBytes(capturedBodyRaw);
  const capturedIsBinary =
    capturedBodyRaw !== null && !("text" in decodeCapturedBody(capturedBodyRaw));

  /** Editing replaces the capture: the body becomes plain manual text again. */
  const editBody = (value: string) => {
    markEdited();
    setBodyPresent(true);
    setCaptureBodyIssue(null);
    setCapturedBodyRaw(null);
    setBody(value);
  };

  // One non-overlapping read per panel scope. Continuous capture itself lives
  // in the guest, so hiding/unmounting the panel does not lose background tabs.
  const followRef = useRef<() => Promise<void>>(async () => {});
  followRef.current = async () => {
    if (!getSnapshot || loading) return;
    const selection = selectionRef.current;
    const next = await getSnapshot();
    if (!mountedRef.current || selection !== selectionRef.current) return;
    if (!next) {setFollowNotice("Suivi indisponible : la capture affichée n'est pas confirmée à jour."); return;}
    const activeChanged = next.activeTabId !== lastActiveRef.current;
    lastActiveRef.current = next.activeTabId;
    if (dirty || followMode === "history") {
      if (dirty) setFollowNotice("Brouillon conservé ; choisir Reprendre le suivi pour les nouvelles captures.");
      return;
    }
    const tabId = (followMode === "browser" || activeChanged) && next.activeTabId
      ? next.activeTabId : selectedTabId;
    if (!tabId) return;
    const tab = next.tabs.find(t=>t.tabId === tabId);
    if (!tab || tab.closed) {setFollowNotice("Onglet fermé : capture conservée, envoi interdit."); return;}
    setSelectedTabId(tabId);
    const latest = next.history.find(row=>row.tabId === tabId);
    if (!latest) {
      setFollowNotice("Aucune requête encore capturée pour cet onglet."); setSelectedCapture(null);
      setMethod("GET"); setUrl(tab.url); setHeaders([{key:"",value:""}]);
      setBody(""); setBodyPresent(false); setCapturedBodyRaw(null); return;
    }
    const bound = selectedCapture?.capture;
    if (bound?.epoch === next.epoch && bound.id === latest.id && bound.revision === latest.revision) return;
    const token = ++selectionRef.current;
    setHistoryEntryState("loading");
    const detail = await getRequestHistoryEntry(latest.id);
    if (!mountedRef.current || selectionRef.current !== token) return;
    if (!detail || detail.tabId !== tabId) {setHistoryEntryState("missing"); return;}
    fillFromCapture(detail);
    setHistoryEntryState("idle");
  };
  useEffect(() => {
    if (!getSnapshot) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = async () => {
      await followRef.current();
      if (!stopped) timer = setTimeout(refresh, 750);
    };
    void refresh();
    return () => {stopped = true; if (timer !== undefined) clearTimeout(timer); selectionRef.current += 1;};
  }, [getSnapshot, sessionId, sessionToken]);

  // Sampled once per render: two rows of the same list must not disagree by a
  // second because Date.now() advanced between them.
  const nowMs = Date.now();
  const summaryTab = selectedTabId ?? snapshot?.activeTabId;
  const summaryCapture = snapshot?.history.find(row => !summaryTab || row.tabId === summaryTab);

  /**
   * Task 215 — the suggestion list, built in memory from what is already
   * loaded. Typing never issues a bridge command.
   *
   * The tab filter is applied on `tabId`, never on the URL: two tabs can sit on
   * the same URL and still be different targets, and grouping them would defeat
   * the whole point of the identity 214 added.
   */
  const needle = url.trim().toLowerCase();
  const visibleTabs =
    selectedTabId === null ? openTabs : openTabs.filter((t) => t.tabId === selectedTabId);
  const visibleHistory =
    selectedTabId === null
      ? requestHistory
      : requestHistory.filter((e) => e.tabId === selectedTabId);
  const matches = (candidateUrl: string) =>
    needle === "" || candidateUrl.toLowerCase().includes(needle);
  const tabSuggestions = visibleTabs.filter((t) => matches(t.url));
  const historySuggestions = visibleHistory.filter((e) => matches(e.url));
  const suggestionCount = tabSuggestions.length + historySuggestions.length;

  const fullBody = response?.body ?? "";
  const truncated = fullBody.length > BODY_PREVIEW_CHARS;
  const shownBody =
    truncated && !responseBodyExpanded ? fullBody.slice(0, BODY_PREVIEW_CHARS) : fullBody;

  const columnStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    padding: 6,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  };

  return (
    <div
      style={{
        borderTop: "1px solid #333",
        borderBottom: "1px solid #333",
        background: "#0a0a0a",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        maxHeight: detailsExpanded ? PANEL_HEIGHT + 160 : undefined,
      }}
    >
      {/* ── Bandeau toujours visible ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 8px",
          borderBottom: detailsExpanded ? "1px solid #222" : "none",
          background: "#111",
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <button
          onClick={toggleDetails}
          aria-expanded={detailsExpanded}
          // Task 219 — the name says what the click will do, not a fixed verb.
          aria-label={
            detailsExpanded
              ? "Replier le panneau shell-browser"
              : "Déplier le panneau shell-browser"
          }
          style={{
            background: "none",
            border: "none",
            color: "#9ca3af",
            cursor: "pointer",
            fontSize: 11,
            padding: "0 2px",
            flexShrink: 0,
          }}
        >
          {detailsExpanded ? "▾" : "▸"}
        </button>
        <span
          style={{ color: "#9CA3AF", fontSize: 11, fontFamily: "monospace", flexShrink: 0 }}
        >
          SHELL-BROWSER
        </span>

        {/* Dernière requête — informatif, indépendant du mode et de la requête */}
        <div
          role="group"
          aria-label="Dernière requête"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 10,
            color: "#6b7280",
            flex: 1,
            minWidth: 0,
          }}
        >
          {getSnapshot ? <>
            {requestHistoryStatus === "unavailable" ? <span>capture indisponible</span> :
              !snapshot ? <span>lecture…</span> : summaryCapture ? <>
                <span>{summaryCapture.method}</span>
                <span title={summaryCapture.url} style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{summaryCapture.url}</span>
                <button type="button" onClick={() => selectHistoryEntry(summaryCapture.id)}>Utiliser</button>
              </> : <span>aucune requête capturée pour cet onglet</span>}
          </> : <>
          {lastRequestStatus === "loading" && <span>lecture…</span>}
          {lastRequestStatus === "available" && lastRequest && (
            <>
              <span style={{ color: "#9ca3af", fontFamily: "monospace", flexShrink: 0 }}>
                {lastRequest.method}
              </span>
              <span
                title={lastRequest.url}
                style={{
                  flex: 1,
                  minWidth: 0,
                  color: "#d1d5db",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {lastRequest.url}
              </span>
              {lastRequest.body === null && (
                <span style={{ color: "#f59e0b", flexShrink: 0 }}>corps indisponible</span>
              )}
              {lastRequest.bodyTruncated && (
                <span style={{ color: "#f59e0b", flexShrink: 0 }}>corps tronqué</span>
              )}
              <button
                onClick={useLastRequest}
                style={{
                  background: "#1f2937",
                  color: "#9ca3af",
                  border: "none",
                  borderRadius: 3,
                  padding: "0 6px",
                  fontSize: 10,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Utiliser
              </button>
            </>
          )}
          {(lastRequestStatus === "idle" || lastRequestStatus === "unavailable") && (
            <span style={{ flex: 1, minWidth: 0 }}>aucune requête capturée</span>
          )}
          </>}
        </div>

        {/* Rafraîchir et fermer : deux actions distinctes, jamais confondues. */}
        <button
          onClick={loadSources}
          disabled={
            lastRequestStatus === "loading" ||
            requestHistoryStatus === "loading" ||
            openTabsStatus === "loading"
          }
          aria-label="Actualiser les sources"
          style={{
            background: "none",
            border: "none",
            color: "#6b7280",
            cursor: "pointer",
            fontSize: 11,
            padding: "0 2px",
            flexShrink: 0,
          }}
        >
          ⟳
        </button>
        <button
          onClick={onClose}
          aria-label="Fermer le panneau shell-browser"
          style={{
            background: "none",
            border: "none",
            color: "#6b7280",
            cursor: "pointer",
            fontSize: 13,
            padding: "2px 4px",
            flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>

      {detailsExpanded && (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <div role="status" aria-label="Suivi de capture" style={{fontSize:11,padding:"4px 8px",color:"var(--text-secondary)"}}>
            {followNotice || (followMode === "history" ? "Capture historique sélectionnée." : "Suivi automatique par onglet.")}
            {snapshot?.overflow && " Limite de capture atteinte : couverture partielle."}
            {selectedCapture?.capture && ` ${selectedCapture.resourceType} · ${selectedCapture.headerProvenance} · cookies : ${selectedCapture.cookieProvenance} · corps : ${selectedCapture.bodyState}.`}
            {(dirty || followMode === "history") && <button type="button" onClick={() => {
              if (!stashDraft()) return;
              setDirty(false); setFollowMode("tab"); setSelectedCapture(null);
              setFollowNotice("Reprise du suivi ; brouillon conservé pour cet onglet.");
            }}>Reprendre le suivi</button>}
          </div>
          {/* ── 1. Bandeau des onglets ouverts ── */}
          <div
            role="group"
            aria-label="Onglets ouverts"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 8px",
              borderBottom: "1px solid #222",
              overflowX: "auto",
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => {if (!stashDraft()) return; selectionRef.current += 1; setSelectedTabId(null); setFollowMode("browser");}}
              aria-pressed={selectedTabId === null}
              style={{
                background: selectedTabId === null ? "#2563eb" : "#1f2937",
                color: selectedTabId === null ? "#fff" : "#9ca3af",
                border: "none",
                borderRadius: 3,
                padding: "1px 8px",
                fontSize: 10,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              Tous
            </button>
            {openTabsStatus === "loading" && (
              <span style={{ color: "#6b7280", fontSize: 10 }}>lecture…</span>
            )}
            {openTabsStatus === "unavailable" && (
              <span style={{ color: "#6b7280", fontSize: 10 }}>onglets indisponibles</span>
            )}
            {openTabsStatus === "available" && openTabs.every(tab => tab.closed) && (
              <span style={{ color: "#6b7280", fontSize: 10 }}>aucun onglet ouvert</span>
            )}
            {openTabs.filter(tab => !tab.closed).map((tab) => (
              <button
                key={tab.tabId}
                onClick={() => selectTab(tab)}
                aria-pressed={selectedTabId === tab.tabId}
                title={tab.url}
                style={{
                  background: selectedTabId === tab.tabId ? "#2563eb" : "#1f2937",
                  color: selectedTabId === tab.tabId ? "#fff" : "#9ca3af",
                  border: "none",
                  borderRadius: 3,
                  padding: "1px 8px",
                  fontSize: 10,
                  cursor: "pointer",
                  flexShrink: 0,
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {tab.title || tab.url}
              </button>
            ))}
          </div>

          {/* ── 2. Omnibox : méthode + URL + suggestions, mode et envoi ── */}
          <div
            style={{
              display: "flex",
              gap: 4,
              padding: "4px 8px",
              borderBottom: "1px solid #222",
              flexShrink: 0,
              position: "relative",
            }}
          >
            <select
              value={method}
              onChange={(e) => {markEdited(); setMethod(e.target.value);}}
              aria-label="Méthode HTTP"
              style={{ ...inputStyle, flexShrink: 0 }}
            >
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
              {!(METHODS as readonly string[]).includes(method) && (
                <option key={method} value={method}>
                  {method}
                </option>
              )}
            </select>
            {/* La liste est en position absolue : sans fermeture au blur elle
                resterait posee sur la colonne Requete, et le clic suivant —
                destine a un champ d'en-tete — atterrirait sur une suggestion.
                Les boutons de suggestion annulent leur mousedown, donc le focus
                ne quitte pas le champ et leur propre clic passe toujours. */}
            <div
              style={{ flex: 1, minWidth: 0, position: "relative" }}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setSuggestionsOpen(false);
                }
              }}
            >
              <input
                value={url}
                onChange={(e) => {
                  markEdited();
                  setUrl(e.target.value);
                  setSuggestionsOpen(true);
                }}
                onFocus={() => setSuggestionsOpen(true)}
                // Apres une selection le champ n'a jamais perdu le focus :
                // sans ceci, `onFocus` ne se redeclenche pas et la liste
                // devient impossible a rouvrir a la souris.
                onClick={() => setSuggestionsOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setSuggestionsOpen(false);
                }}
                placeholder="Chercher un onglet ou une capture, ou saisir une URL"
                aria-label="URL de la requête"
                maxLength={16384}
                role="combobox"
                aria-expanded={suggestionsOpen}
                aria-controls="shell-omnibox-suggestions"
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
              />
              {suggestionsOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 5,
                    background: "#111",
                    border: "1px solid #333",
                    borderRadius: 3,
                    maxHeight: 180,
                    overflowY: "auto",
                  }}
                >
                  {/* Task 215 : l'etat des sources reste visible, comme il
                      l'etait dans la liste deroulante de 213 — une liste vide
                      parce qu'on lit et une liste vide parce que le bridge ne
                      repond pas ne sont pas la meme chose. */}
                  {(requestHistoryStatus === "loading" || openTabsStatus === "loading") && (
                    <div style={{ padding: "3px 6px", color: "#6b7280", fontSize: 10 }}>
                      lecture…
                    </div>
                  )}
                  {requestHistoryStatus === "unavailable" && (
                    <div style={{ padding: "3px 6px", color: "#6b7280", fontSize: 10 }}>
                      historique indisponible
                    </div>
                  )}
                  {suggestionCount === 0 &&
                    requestHistoryStatus !== "loading" &&
                    requestHistoryStatus !== "unavailable" &&
                    openTabsStatus !== "loading" && (
                      <div style={{ padding: "3px 6px", color: "#6b7280", fontSize: 10 }}>
                        aucune suggestion
                      </div>
                    )}
                  <ul
                    id="shell-omnibox-suggestions"
                    role="listbox"
                    aria-label="Suggestions"
                    style={{ margin: 0, padding: 0, listStyle: "none" }}
                  >
                  {tabSuggestions.map((tab) => (
                    <li key={`tab:${tab.tabId}`} role="option" aria-selected={false}>
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectTab(tab)}
                        style={{
                          display: "flex",
                          gap: 6,
                          width: "100%",
                          textAlign: "left",
                          background: "none",
                          border: "none",
                          color: "#d1d5db",
                          fontSize: 10,
                          padding: "3px 6px",
                          cursor: "pointer",
                          minWidth: 0,
                        }}
                      >
                        <span style={{ color: "#6b7280", flexShrink: 0 }}>Onglet</span>
                        {tab.title && (
                          <span style={{ color: "#e5e7eb", flexShrink: 0, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {tab.title}
                          </span>
                        )}
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {tab.url}
                        </span>
                      </button>
                    </li>
                  ))}
                  {historySuggestions.map((entry) => (
                    <li key={`history:${entry.id}`} role="option" aria-selected={false}>
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectHistoryEntry(entry.id)}
                        style={{
                          display: "flex",
                          gap: 6,
                          width: "100%",
                          textAlign: "left",
                          background: "none",
                          border: "none",
                          color: "#d1d5db",
                          fontSize: 10,
                          padding: "3px 6px",
                          cursor: "pointer",
                          minWidth: 0,
                        }}
                      >
                        <span style={{ color: "#6b7280", flexShrink: 0 }}>Historique</span>
                        <span style={{ color: "#9ca3af", fontFamily: "monospace", flexShrink: 0 }}>
                          {entry.method}
                        </span>
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {entry.url}
                        </span>
                        <span style={{ color: "#6b7280", flexShrink: 0 }}>
                          {entry.resourceType ? `${entry.resourceType} · ` : ""}{relativeTime(entry.timestamp, nowMs)}
                        </span>
                      </button>
                    </li>
                  ))}
                  </ul>
                </div>
              )}
            </div>
            {/* Task 219 — Browser → Shell-Browser. The opposite direction was
                dropped by the user: no button, no code for it. */}
            <button
              type="button"
              onClick={() => {
                void syncFromBrowser();
              }}
              disabled={syncState === "loading"}
              aria-label="Synchroniser depuis l'onglet actif du navigateur"
              title="Remplit le formulaire depuis l'onglet Chrome réellement actif : sa capture si elle existe, sinon URL + GET + cookies réels"
              style={{
                background: "#1f2937",
                color: syncState === "loading" ? "#6b7280" : "#9ca3af",
                border: "none",
                borderRadius: 3,
                padding: "2px 8px",
                fontSize: 11,
                cursor: syncState === "loading" ? "not-allowed" : "pointer",
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              {syncState === "loading" ? "Sync…" : "Sync B→SB"}
            </button>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as ShellRequestMode)}
              aria-label="Mode d'exécution"
              style={{ ...inputStyle, flexShrink: 0 }}
            >
              <option value="in_page">In-page</option>
              <option value="guest_proxy">Backend → VM</option>
            </select>
            <button
              onClick={submit}
              disabled={loading || historyEntryState === "loading" || !selectedTabId || (selectedCapture?.capture !== undefined &&
                (selectedCapture.headersState !== "complete" || (!dirty &&
                  !["absent", "complete"].includes(selectedCapture.bodyState ?? "unavailable")))) ||
                (selectedTabId !== null && openTabs.some(tab=>tab.tabId === selectedTabId && tab.closed))}
              aria-label="Envoyer la requête"
              style={{
                background: loading ? "#374151" : "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 3,
                padding: "2px 10px",
                fontSize: 11,
                cursor: loading ? "not-allowed" : "pointer",
                flexShrink: 0,
              }}
            >
              {loading ? "…" : "Envoyer"}
            </button>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "0 8px 4px",
              flexShrink: 0,
            }}
          >
            <span style={{ color: "#6b7280", fontSize: 10 }}>{MODE_SUBTITLES[mode]}</span>
            {historyEntryState === "loading" && (
              <span style={{ color: "#6b7280", fontSize: 10 }}>lecture…</span>
            )}
            {historyEntryState === "missing" && (
              <span style={{ color: "#f59e0b", fontSize: 10 }}>entrée expirée</span>
            )}
            {captureBodyIssue !== null && (
              <span role="status" style={{ fontSize: 10 }}>
                {captureBodyIssue === "unavailable" ? "Corps capturé indisponible" : "Corps capturé incomplet ou tronqué"}
                {" — vérifiez le corps avant envoi"}
              </span>
            )}
            {/* Task 219 — the Sync says what it is doing and what it could not
                read. A failed page read leaves the form alone; a failed cookie
                read still fills URL + GET, and says the Cookie line is missing. */}
            {syncState === "loading" && (
              <span style={{ color: "#6b7280", fontSize: 10 }}>synchronisation…</span>
            )}
            {syncState === "page_unavailable" && (
              <span style={{ color: "#f59e0b", fontSize: 10 }}>onglet actif illisible</span>
            )}
            {syncState === "cookies_unavailable" && (
              <span style={{ color: "#f59e0b", fontSize: 10 }}>
                cookies illisibles — en-tête Cookie omis
              </span>
            )}
          </div>

          {/* ── 3. Requête et Réponse, côte à côte ── */}
          <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
            <section
              aria-label="Requête"
              style={{ ...columnStyle, borderRight: "1px solid #222" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: "#6b7280", fontSize: 10 }}>En-têtes</span>
                <button
                  onClick={addHeader}
                  aria-label="Ajouter un en-tête"
                  style={{
                    background: "#1f2937",
                    color: "#9ca3af",
                    border: "none",
                    borderRadius: 3,
                    padding: "0 6px",
                    fontSize: 11,
                    cursor: "pointer",
                  }}
                >
                  +
                </button>
              </div>
              {headers.map((header, index) => {
                const controlled =
                  mode === "in_page"
                    ? isBrowserControlledHeader(header.key)
                    : isRelayRejectedHeader(header.key);
                return (
                  <div key={index} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <input
                      value={header.key}
                      onChange={(e) => updateHeader(index, { key: e.target.value })}
                      placeholder="Nom"
                      aria-label={`Nom de l'en-tête ${index + 1}`}
                      style={{ ...inputStyle, width: 110, flexShrink: 0 }}
                    />
                    <input
                      value={header.value}
                      onChange={(e) => updateHeader(index, { value: e.target.value })}
                      placeholder="Valeur"
                      aria-label={`Valeur de l'en-tête ${index + 1}`}
                      style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                    />
                    <button
                      onClick={() => removeHeader(index)}
                      aria-label={`Supprimer l'en-tête ${index + 1}`}
                      style={{
                        background: "#1f2937",
                        color: "#9ca3af",
                        border: "none",
                        borderRadius: 3,
                        padding: "0 6px",
                        fontSize: 11,
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      ✕
                    </button>
                    {controlled && (
                      <span
                        title={
                          mode === "in_page"
                            ? "En-tête contrôlé par le navigateur : Chrome peut l'ignorer."
                            : "En-tête refusé par le relais : le backend en est le propriétaire."
                        }
                        style={{ color: "#f59e0b", fontSize: 10, flexShrink: 0 }}
                      >
                        {mode === "in_page" ? "navigateur" : "refusé"}
                      </span>
                    )}
                  </div>
                );
              })}

              {capturedIsBinary && capturedBytes && (
                <div style={{ fontSize: 10, color: "#f59e0b" }}>
                  Corps binaire ({capturedBytes.length} octets) — non affiché
                </div>
              )}
              <textarea
                maxLength={1048576}
                value={body}
                onChange={(e) => editBody(e.target.value)}
                placeholder={method === "GET" ? "Corps ignoré pour GET" : "Corps (texte / JSON)"}
                aria-label="Corps de la requête"
                rows={3}
                style={{ ...inputStyle, resize: "none", fontFamily: "monospace" }}
              />
            </section>

            <section aria-label="Réponse" style={columnStyle}>
              {loading && (
                <div style={{ color: "#9ca3af", fontSize: 11 }}>Requête en cours dans la page…</div>
              )}

              {transportError && !loading && (
                <div
                  style={{
                    background: "#7f1d1d",
                    color: "#fca5a5",
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: 11,
                  }}
                >
                  <strong>{mode === "in_page" ? "Transport CDP" : "Relais VM"}</strong> —{" "}
                  {transportError}
                </div>
              )}

              {pageError && !loading && (
                <div
                  style={{
                    background: "#78350f",
                    color: "#fcd34d",
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: 11,
                  }}
                >
                  <strong>{PAGE_ERROR_LABELS[pageError.kind]}</strong> — {pageError.message}
                  {pageError.kind === "opaque" && (
                    <div style={{ marginTop: 2, color: "#fde68a" }}>
                      Aucun statut, en-tête ni corps n'est lisible : le navigateur masque
                      entièrement une réponse opaque.
                    </div>
                  )}
                  {/*
                    Task 211 — proposer l'autre mode, sans rien décider à la place de
                    l'utilisateur. Le garde `mode === "in_page"` fait disparaître le
                    bouton une fois basculé : il ne propose jamais une transition
                    déjà faite. « peut contourner » et non « corrige » : la nature
                    `cors_or_network` regroupe aussi une vraie panne DNS ou TLS, que
                    Backend → VM ne réparerait pas.
                  */}
                  {pageError.kind === "cors_or_network" && mode === "in_page" && !selectedCapture?.capture && (
                    <div
                      style={{
                        marginTop: 4,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      <span style={{ color: "#fde68a" }}>
                        Le mode Backend → VM peut contourner cette limite.
                      </span>
                      <button
                        type="button"
                        onClick={() => setMode("guest_proxy")}
                        style={{
                          background: "#1f2937",
                          color: "#9ca3af",
                          border: "none",
                          borderRadius: 3,
                          padding: "1px 8px",
                          fontSize: 10,
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        Basculer vers Backend → VM
                      </button>
                    </div>
                  )}
                </div>
              )}

              {response && !loading && (
                <div style={{ fontSize: 11, color: "#d1d5db" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span
                      style={{
                        color: response.status < 400 ? "#6ee7b7" : "#fca5a5",
                        fontFamily: "monospace",
                        fontWeight: 600,
                      }}
                    >
                      {response.status} {response.statusText}
                    </span>
                    <span style={{ color: "#6b7280" }}>{response.durationMs} ms</span>
                    {response.responseType && (
                      <span style={{ color: "#6b7280" }}>type: {response.responseType}</span>
                    )}
                    {typeof response.cookiesApplied === "number" && (
                      <span style={{ color: "#6b7280" }}>
                        {response.cookiesApplied} cookie(s) rejoué(s)
                      </span>
                    )}
                    {response.truncated && (
                      <span style={{ color: "#f59e0b" }}>corps tronqué par le relais</span>
                    )}
                    {response.bodyEncoding === "base64" && (
                      <span style={{ color: "#f59e0b" }}>corps binaire (base64)</span>
                    )}
                  </div>

                  {response.sourceTabId && (
                    <div style={{color:response.sourceTabId === selectedTabId ? "#9ca3af" : "#fbbf24"}}>
                      Réponse de l’onglet {response.sourceTabId}
                      {response.sourceCaptureId !== undefined && ` · capture ${response.sourceCaptureId}`}
                      {response.nativeTransport && ` · ${response.nativeTransport}`}
                      {response.sourceTabId !== selectedTabId && " · autre onglet que la sélection actuelle"}
                    </div>
                  )}
                  {!!response.fidelityLimits?.length && (
                    <div style={{color:"#fbbf24"}}>Limites déclarées : {response.fidelityLimits.join(" ; ")}</div>
                  )}
                  <div
                    style={{ marginTop: 4, color: "#9ca3af", fontFamily: "monospace", fontSize: 10 }}
                  >
                    {Object.entries(response.headers).map(([key, value]) => (
                      <div key={key}>
                        <span style={{ color: "#6b7280" }}>{key}:</span> {value}
                      </div>
                    ))}
                  </div>

                  <pre
                    style={{
                      marginTop: 4,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      fontFamily: "monospace",
                      fontSize: 10,
                      color: "#d1d5db",
                    }}
                  >
                    {shownBody}
                  </pre>
                  {truncated && (
                    <button
                      onClick={() => setResponseBodyExpanded((v) => !v)}
                      style={{
                        background: "#1f2937",
                        color: "#9ca3af",
                        border: "none",
                        borderRadius: 3,
                        padding: "2px 8px",
                        fontSize: 10,
                        cursor: "pointer",
                      }}
                    >
                      {responseBodyExpanded
                        ? "Réduire"
                        : `Voir tout (${fullBody.length} caractères)`}
                    </button>
                  )}
                </div>
              )}

              {!loading && !response && !pageError && !transportError && (
                <div style={{ color: "#4b5563", fontSize: 11 }}>
                  {mode === "in_page"
                    ? "Aucune requête envoyée. La requête part du Chrome de la session : cookies éligibles, User-Agent et IP de sortie sont ceux de la VM."
                    : "Aucune requête envoyée. La requête part de la VM avec les cookies de la session rejoués par le backend ; l'empreinte TLS et HTTP est celle du client de la VM, pas celle de Chrome."}
                </div>
              )}
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
